export type NotchMode = "off" | "50hz" | "60hz";

export function normalizeNotchFlags(
  notchFilter60Hz: boolean,
  notchFilter50Hz?: boolean,
): {
  notch_filter_60hz: boolean;
  notch_filter_50hz: boolean;
} {
  const notch60 = Boolean(notchFilter60Hz);
  const notch50 = Boolean(notchFilter50Hz) && !notch60;
  return {
    notch_filter_60hz: notch60,
    notch_filter_50hz: notch50,
  };
}

export function mergeNotchFlags(
  current: Pick<
    { notch_filter_60hz: boolean; notch_filter_50hz?: boolean },
    "notch_filter_60hz" | "notch_filter_50hz"
  >,
  updates: Partial<
    Pick<
      { notch_filter_60hz: boolean; notch_filter_50hz?: boolean },
      "notch_filter_60hz" | "notch_filter_50hz"
    >
  >,
): {
  notch_filter_60hz: boolean;
  notch_filter_50hz: boolean;
} {
  const hasNotch60 = typeof updates.notch_filter_60hz === "boolean";
  const hasNotch50 = typeof updates.notch_filter_50hz === "boolean";
  let nextNotch60 = hasNotch60
    ? Boolean(updates.notch_filter_60hz)
    : Boolean(current.notch_filter_60hz);
  let nextNotch50 = hasNotch50
    ? Boolean(updates.notch_filter_50hz)
    : Boolean(current.notch_filter_50hz);

  if (hasNotch60 && nextNotch60) {
    nextNotch50 = false;
  }
  if (hasNotch50 && nextNotch50) {
    nextNotch60 = false;
  }

  return normalizeNotchFlags(nextNotch60, nextNotch50);
}

export function getNotchModeFromFlags(
  notchFilter60Hz: boolean,
  notchFilter50Hz?: boolean,
): NotchMode {
  if (notchFilter60Hz) return "60hz";
  if (notchFilter50Hz) return "50hz";
  return "off";
}

export function getNotchFlagsForMode(mode: NotchMode): {
  notch_filter_60hz: boolean;
  notch_filter_50hz: boolean;
} {
  if (mode === "60hz") {
    return {
      notch_filter_60hz: true,
      notch_filter_50hz: false,
    };
  }
  if (mode === "50hz") {
    return {
      notch_filter_60hz: false,
      notch_filter_50hz: true,
    };
  }
  return {
    notch_filter_60hz: false,
    notch_filter_50hz: false,
  };
}

export function getNotchModeLabel(mode: NotchMode): string {
  if (mode === "60hz") return "60Hz";
  if (mode === "50hz") return "50Hz";
  return "Off";
}

const NOTCH_METADATA_NUMERIC_KEYS = [
  "mains_frequency_hz",
  "mains_frequency",
  "line_frequency_hz",
  "line_frequency",
  "power_line_frequency_hz",
  "power_line_frequency",
  "powerline_frequency_hz",
  "powerline_frequency",
  "notch_frequency_hz",
  "notch_frequency",
  "notch_hz",
  "notch",
] as const;

const NOTCH_METADATA_TEXT_KEYS = [
  "prefilter",
  "prefiltering",
  "filtering",
  "line_noise",
  "line_noise_hz",
  "mains",
  "mains_hz",
  "mains_frequency",
  "powerline",
  "powerline_hz",
] as const;

function parseNotchModeFromMetadataNumber(value: unknown): NotchMode | null {
  const numeric =
    typeof value === "number"
      ? value
      : Number.parseFloat(typeof value === "string" ? value : "");
  if (!Number.isFinite(numeric)) return null;
  if (Math.abs(numeric - 50) <= 1) return "50hz";
  if (Math.abs(numeric - 60) <= 1) return "60hz";
  return null;
}

function parseNotchModeFromMetadataText(value: unknown): NotchMode | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;
  if (/^(off|none|disabled?)$/i.test(text)) return "off";

  const explicitMatch =
    text.match(
      /\b(?:notch|line(?:\s*noise)?|mains|power\s*line|n)\s*[:=]?\s*(50|60)\s*hz?\b/i,
    ) ||
    text.match(
      /\b(50|60)\s*hz?\s*(?:notch|line(?:\s*noise)?|mains|power\s*line)\b/i,
    );
  if (explicitMatch?.[1] === "50") return "50hz";
  if (explicitMatch?.[1] === "60") return "60hz";

  return parseNotchModeFromMetadataNumber(text);
}

function inferNotchModeFromMetadataObject(
  source: Record<string, unknown>,
): NotchMode | null {
  for (const key of NOTCH_METADATA_NUMERIC_KEYS) {
    const parsed = parseNotchModeFromMetadataNumber(source[key]);
    if (parsed) return parsed;
  }
  for (const key of NOTCH_METADATA_TEXT_KEYS) {
    const parsed = parseNotchModeFromMetadataText(source[key]);
    if (parsed) return parsed;
  }
  return null;
}

function inferNotchModeFromEdfSignalMetadata(value: unknown): NotchMode | null {
  if (!Array.isArray(value)) return null;
  let count50 = 0;
  let count60 = 0;

  value.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const row = entry as Record<string, unknown>;
    const candidates = [
      row.prefilter,
      row.reserved,
      row.transducer,
      row.filtering,
    ];
    candidates.forEach((candidate) => {
      const parsed = parseNotchModeFromMetadataText(candidate);
      if (parsed === "50hz") count50 += 1;
      if (parsed === "60hz") count60 += 1;
    });
  });

  if (count50 === 0 && count60 === 0) return null;
  return count50 >= count60 ? "50hz" : "60hz";
}

export function inferNotchModeFromRecordingMetadata(
  recording: unknown,
): NotchMode | null {
  if (!recording || typeof recording !== "object") return null;
  const row = recording as Record<string, unknown>;

  const direct = inferNotchModeFromMetadataObject(row);
  if (direct) return direct;

  const nestedCandidates = [
    row.metadata,
    row.derivation_metadata,
    row.filter_config,
    row.filters,
    row.eeg_metadata,
    row.edf_metadata,
  ];
  for (const nested of nestedCandidates) {
    if (!nested || typeof nested !== "object") continue;
    const parsed = inferNotchModeFromMetadataObject(
      nested as Record<string, unknown>,
    );
    if (parsed) return parsed;
  }

  const fromSignalRows = inferNotchModeFromEdfSignalMetadata(
    row.edf_signal_metadata,
  );
  if (fromSignalRows) return fromSignalRows;

  return null;
}
